<i18n src="./resources/locales.json"></i18n>

<template>
  <div>
    <v-tour class="sms-tour-container" name="sms-tour" :steps="steps">
      <template v-slot="tour">
        <v-step
          v-if="tour.currentStep !== -1"
          :key="tour.currentStep"
          :step="tour.steps[tour.currentStep]"
          :previous-step="tour.previousStep"
          :next-step="tour.nextStep"
          :stop="tour.stop"
          :skip="tour.skip"
          :is-first="tour.isFirst"
          :is-last="tour.isLast"
          :labels="tour.labels"
          class="surface lighten-1"
        >
          <template v-slot:header>
            <div class="v-step__header surface lighten-1">
              {{ tour.steps[tour.currentStep].header.title }}
              <v-btn v-if="!tour.isFirst" class="close-btn" text dense x-small icon @click="tour.skip">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
          </template>
          <template v-slot:content>
            <div class="v-step__content surface lighten-1">
              {{ tour.steps[tour.currentStep].content }}
            </div>
            <span class="sms-tour-counter"> {{ tour.currentStep + 1 }} / {{ steps.length }} </span>
          </template>
          <template v-slot:actions>
            <div class="surface lighten-1">
              <v-btn v-if="tour.isFirst" dense small class="mr-2" @click="tour.skip">
                {{ $t('tourButtons.skip') }}
              </v-btn>
              <v-btn v-if="!tour.isFirst" dense small class="mr-2" @click="tour.previousStep">
                {{ $t('tourButtons.previous') }}
              </v-btn>
              <v-btn
                v-if="!tour.isLast"
                dense
                small
                class="mr-2 sms-primary-button"
                color="primary"
                @click="tour.nextStep"
              >
                {{ $t('tourButtons.next') }}
              </v-btn>
              <v-btn v-if="tour.isLast" dense small class="sms-primary-button" color="primary" @click="tour.finish">
                {{ $t('tourButtons.finish') }}
              </v-btn>
            </div>
          </template>
        </v-step>
      </template>
    </v-tour>
    <v-dialog v-model="showWelcomeDialog" width="500" persistent>
      <v-card>
        <v-card-title class="sms-primary-area sms-welcome-dialog-title">
          <span class="headline">{{ $t('dialog.title') }}</span>
        </v-card-title>
        <v-card-text class="sms-welcome-dialog-content">
          <span> {{ $t('dialog.content') }} </span>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="closeWelcomeDialog">
            {{ $t('dialog.close') }}
          </v-btn>
          <v-btn color="sms-primary-button" @click="initTour">
            {{ $t('dialog.initTour') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'SmsApplicationTour',

  data() {
    return {
      steps: this._getBasicCompleteTourSteps(), // IMPORTANT: Always send steps if not the v-tour component fails.
      showWelcomeDialog: false
    };
  },

  mounted() {
    this.$root.$on('launch-sms-tour', () => {
      this.showWelcomeDialog = true;
    });
  },

  beforeDestroy() {
    this.$root.$off('launch-sms-tour');
  },

  methods: {
    _getBasicCompleteTourSteps() {
      // There is an open issue in i18n that can not be used by referring inside objects.
      // https://github.com/kazupon/vue-i18n/issues/259
      const translate = key => this.$t(key);

      return [
        // Navigation
        {
          target: '#sms-navigation',
          header: {
            title: translate('navigation.title')
          },
          content: translate('navigation.content'),
          params: {
            placement: 'top'
          }
        },
        // Settings
        {
          target: '#sms-settings-tab',
          header: {
            title: translate('settings.title')
          },
          content: translate('settings.content'),
          params: {
            placement: 'top'
          },
          before: () =>
            new Promise((resolve, reject) => {
              const tab = document.getElementById('sms-settings-tab');
              if (tab) {
                tab.click();
                resolve();
              } else {
                reject();
              }
            })
        },
        {
          target: '#sms-settings-display',
          header: {
            title: translate('settings.display.title')
          },
          content: translate('settings.display.content'),
          params: {
            placement: 'top'
          }
        },
        {
          target: '#sms-settings-additional',
          header: {
            title: translate('settings.additional.title')
          },
          content: translate('settings.additional.content'),
          params: {
            placement: 'top'
          },
          before: () =>
            new Promise((resolve, reject) => {
              const tab = document.getElementById('sms-settings-tab');
              if (tab) {
                tab.click();
                resolve();
              } else {
                reject();
              }
            })
        },
        // Contacts
        {
          target: '#sms-contacts-tab',
          header: {
            title: translate('contacts.browse.title')
          },
          content: translate('contacts.browse.content'),
          params: {
            placement: 'top'
          },
          before: () =>
            new Promise((resolve, reject) => {
              const btn = document.getElementById('sms-contacts-tab');
              if (btn) {
                btn.click();
                resolve();
              } else {
                reject();
              }
            })
        },
        {
          target: '#sms-side-panel',
          header: {
            title: translate('contacts.actions.title')
          },
          content: translate('contacts.actions.content'),
          params: {
            placement: 'left'
          },
          before: () =>
            new Promise((resolve, reject) => {
              const btn = document.getElementById('sms-contacts-tab');
              if (btn) {
                btn.click();
                resolve();
              } else {
                reject();
              }
            })
        },
        // Messages
        {
          target: '#sms-messages-tab',
          header: {
            title: translate('messages.title')
          },
          content: translate('messages.content'),
          params: {
            placement: 'top'
          },
          before: () =>
            new Promise((resolve, reject) => {
              const btn = document.getElementById('sms-messages-tab');
              if (btn) {
                btn.click();
                resolve();
              } else {
                reject();
              }
            })
        },
        {
          target: '#sms-side-panel',
          header: {
            title: translate('messages.selectMessage.title')
          },
          content: translate('messages.selectMessage.content'),
          params: {
            placement: 'left'
          }
        },

        // New Message
        {
          target: '#sms-create-new-message-btn',
          header: {
            title: translate('messages.newMessage.title')
          },
          content: translate('messages.newMessage.content'),
          params: {
            placement: 'top'
          },
          before: () =>
            new Promise((resolve, reject) => {
              this.$root.$emit('open-sidebar');
              const btn = document.getElementById('sms-create-new-message-btn');
              if (btn) {
                resolve();
              } else {
                reject();
              }
            })
        },
        {
          target: '#sms-contact-number-input',
          header: {
            title: translate('messages.enterNumber.title')
          },
          content: translate('messages.enterNumber.content'),
          params: {
            placement: 'top'
          },
          before: () =>
            new Promise((resolve, reject) => {
              const btn = document.getElementById('sms-create-new-message-btn');
              if (btn) {
                btn.click();
                resolve();
              } else {
                reject();
              }
            })
        },
        {
          target: '#sms-send-new-message-input-container',
          header: {
            title: translate('messages.enterMessage.title')
          },
          content: translate('messages.enterMessage.content'),
          params: {
            placement: 'bottom'
          }
        },
        {
          target: '#sms-send-new-message-btn',
          header: {
            title: translate('messages.sendMessage.title')
          },
          content: translate('messages.sendMessage.content'),
          params: {
            placement: 'bottom'
          }
        }
      ];
    },
    initTour() {
      this.showWelcomeDialog = false;
      this.steps = this._getBasicCompleteTourSteps();
      this.$tours['sms-tour'].start();
    },
    closeWelcomeDialog() {
      this.showWelcomeDialog = false;
    }
  }
};
</script>

<style>
.sms-welcome-dialog-title {
  word-break: normal;
}

.sms-welcome-dialog-content {
  padding-top: 24px !important;
  font-size: 1rem;
}

.sms-tour-container {
  z-index: 1000;
}

.sms-tour-container .close-btn {
  position: absolute;
  top: 5px;
  right: 5px;
}

.sms-tour-container .sms-tour-counter {
  position: absolute;
  bottom: 42px;
  right: 5px;
  font-size: 0.75rem;
}

.v-step {
  color: inherit !important;
}

.sms-tour-container .v-step__header {
  max-width: 270px !important;
  color: inherit;
  font-size: 1rem;
  font-weight: bolder;
}

.sms-tour-container .v-step__content {
  max-width: 240px !important;
  color: inherit;
  font-size: 0.95rem;
}
</style>
